import { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Routes, Route, NavLink } from 'react-router-dom'
import { useSession } from './contexts/SessionContext'
import { UserProfileDropdown } from './components/UserProfileDropdown'
import { ThemeToggle, AccessibleToggle } from './components/wondercare'
import { getApiBase } from './lib/env'
import { lazyImport } from './lib/lazy-with-reload'
import './App.css'

const BusinessGrowthDashboard = lazy(() =>
  lazyImport(() => import('./BusinessGrowthDashboard')),
)
const FunnelDashboard = lazy(() =>
  lazyImport(() => import('./FunnelDashboard')),
)
const PenetrationDashboard = lazy(() =>
  lazyImport(() => import('./PenetrationDashboard')),
)

function RouteFallback() {
  return <div className="health-check">Loading…</div>
}

function App() {
  const { user, signOut } = useSession()
  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || null
  const [healthStatus, setHealthStatus] = useState<'checking' | 'ok' | 'error'>('checking')

  useEffect(() => {
    const apiBase = getApiBase();
    fetch(`${apiBase}/health`)
      .then(response => response.json())
      .then(body => {
        setHealthStatus(body?.data?.ts ? 'ok' : 'error')
      })
      .catch(() => {
        setHealthStatus('error')
      })
  }, [])

  if (healthStatus === 'checking') {
    return <div className="health-check">Connecting to server...</div>
  }

  if (healthStatus === 'error') {
    return <div className="health-check error">Unable to connect to server. Please check that the backend is running.</div>
  }

  return (
    <BrowserRouter>
      <nav className="dashboard-nav">
        <div className="dashboard-nav-links">
          <NavLink to="/" end className={({ isActive }) => isActive ? 'active' : ''}>
            Business Growth
          </NavLink>
          <NavLink to="/funnel" className={({ isActive }) => isActive ? 'active' : ''}>
            Funnel Analytics
          </NavLink>
          <NavLink to="/penetration" className={({ isActive }) => isActive ? 'active' : ''}>
            Customer Penetration
          </NavLink>
        </div>
        <div className="dashboard-nav-controls">
          <ThemeToggle />
          <AccessibleToggle />
          <UserProfileDropdown
            displayName={displayName}
            email={user?.email ?? null}
            onSignOut={signOut}
          />
        </div>
      </nav>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<BusinessGrowthDashboard />} />
          <Route path="/funnel" element={<FunnelDashboard />} />
          <Route path="/penetration" element={<PenetrationDashboard />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
